import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from 'recharts';
import { FiArrowLeft, FiAlertCircle, FiArrowRight, FiUpload } from 'react-icons/fi';
import { diagnosisApi } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';

// ── Constants ──────────────────────────────────────────────────────────────────

const TEAL = '#5AAFA7';

const BREAKDOWN_ORDER = [
  'skill_coverage', 'clarity', 'impact_quantification',
  'structural_completeness', 'professional_relevance', 'narrative_strength',
];

const BREAKDOWN_LABELS = {
  skill_coverage: 'Skill Coverage',
  clarity: 'Clarity',
  impact_quantification: 'Impact',
  structural_completeness: 'Structure',
  professional_relevance: 'Relevance',
  narrative_strength: 'Narrative',
};

const BREAKDOWN_FULL_LABELS = {
  skill_coverage: 'Skill Coverage',
  clarity: 'Clarity of Expression',
  impact_quantification: 'Impact Quantification',
  structural_completeness: 'Structural Completeness',
  professional_relevance: 'Professional Relevance',
  narrative_strength: 'Narrative Strength',
};

const BREAKDOWN_COLORS = {
  skill_coverage: '#3b82f6',
  clarity: '#5AAFA7',
  impact_quantification: '#f59e0b',
  structural_completeness: '#8b5cf6',
  professional_relevance: '#10b981',
  narrative_strength: '#ec4899',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function statusColor(status) {
  return status === 'green' ? '#10b981' : status === 'yellow' ? '#f59e0b' : '#ef4444';
}

function StatusBadge({ status, label }) {
  const bg = status === 'green' ? '#dcfce7' : status === 'yellow' ? '#fef9c3' : '#fee2e2';
  const color = statusColor(status);
  const dot = status === 'green' ? '🟢' : status === 'yellow' ? '🟡' : '🔴';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: bg, color,
    }}
    >
      {dot} {label}
    </span>
  );
}

// ── Score Gauge ────────────────────────────────────────────────────────────────

function ScoreGauge({ score = 0, size = 120, status }) {
  const color = statusColor(status || (score >= 80 ? 'green' : score >= 60 ? 'yellow' : 'red'));
  const data = [
    { value: score, fill: color },
    { value: 100 - score, fill: '#f1f5f9' },
  ];
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="85%"
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            paddingAngle={0}
          >
            {data.map((e, i) => <Cell key={i} fill={e.fill} stroke="none" />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}
      >
        <span style={{ fontSize: size * 0.22, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: size * 0.09, color: '#94a3b8' }}>/ 100</span>
      </div>
    </div>
  );
}

// ── Dimension card (0–10 scale) ────────────────────────────────────────────────

function DimCard({ dimKey, label, score, status, explanation }) {
  const dimColor = BREAKDOWN_COLORS[dimKey] || statusColor(status);
  const statusLabel = status === 'green' ? 'Strong' : status === 'yellow' ? 'Needs Work' : 'Critical';
  return (
    <div style={{
      background: '#fff', borderRadius: 10, padding: '12px 14px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${dimColor}`,
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <StatusBadge status={status} label={statusLabel} />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginBottom: 6 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
          {typeof score === 'number' ? score.toFixed(1) : '—'}
        </span>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>/ 10</span>
      </div>
      <div style={{ height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{
          width: `${(score || 0) * 10}%`, height: '100%',
          background: dimColor, borderRadius: 4, transition: 'width 0.8s',
        }}
        />
      </div>
      <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>{explanation}</p>
    </div>
  );
}

// ── Issue card ─────────────────────────────────────────────────────────────────

function IssueCard({ issue, rank }) {
  const [expanded, setExpanded] = useState(rank === 1);
  const sevColor = rank === 1 ? '#ef4444' : '#f59e0b';
  const sevBg = rank === 1 ? '#fee2e2' : '#fef9c3';

  return (
    <div style={{
      background: '#fff', borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
      borderLeft: `5px solid ${sevColor}`,
    }}
    >
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', padding: '16px 20px', display: 'flex', alignItems: 'flex-start',
          gap: 14, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          background: sevBg, color: sevColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 14, marginTop: 1,
        }}
        >{rank}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{issue.title}</span>
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10,
              background: sevBg, color: sevColor, fontWeight: 700,
            }}
            >
              {rank === 1 ? '🔴 High impact' : '🟡 Medium impact'}
            </span>
          </div>
          {!expanded && (
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0', lineHeight: 1.4 }}>
              Primary: <strong>{issue.primary_dimension}</strong>
            </p>
          )}
        </div>
        <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0, marginTop: 6 }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 20px 18px 64px' }}>
          <div style={{
            background: '#f8fafc', borderLeft: '3px solid #cbd5e1',
            borderRadius: '0 6px 6px 0', padding: '10px 14px', marginBottom: 12,
          }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Evidence from your resume
            </span>
            <p style={{ fontSize: 13, color: '#334155', margin: '4px 0 0', fontStyle: 'italic', lineHeight: 1.5 }}>
              &quot;{issue.evidence}&quot;
            </p>
          </div>
          <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px', lineHeight: 1.6 }}>
            <strong>Why it matters:</strong> {issue.why_it_matters}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 10,
              background: '#fee2e2', color: '#ef4444', fontWeight: 600,
            }}
            >
              Primary: {issue.primary_dimension}
            </span>
            {issue.secondary_dimension && (
              <span style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 10,
                background: '#fef9c3', color: '#d97706', fontWeight: 600,
              }}
              >
                Also affects: {issue.secondary_dimension}
              </span>
            )}
            <Link
              to="/resumes"
              style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 12, fontWeight: 700, color: TEAL, textDecoration: 'none',
                border: `1px solid ${TEAL}`, borderRadius: 8, padding: '4px 12px',
              }}
            >
              Fix in Resume Editor <FiArrowRight size={11} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Fix Plan card ──────────────────────────────────────────────────────────────

function FixCard({ action, index }) {
  const accent = index === 0 ? TEAL : '#6366f1';
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '18px 20px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
      borderTop: `4px solid ${accent}`,
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: accent, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13,
        }}
        >{index + 1}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{action.action_title}</span>
        <span style={{
          marginLeft: 'auto', flexShrink: 0,
          fontSize: 11, padding: '3px 10px', borderRadius: 10,
          background: '#f0fdf4', color: '#16a34a', fontWeight: 700,
        }}
        >⏱ {action.time_needed} min
        </span>
      </div>
      <p style={{ fontSize: 13, color: '#475569', margin: '0 0 8px', lineHeight: 1.5 }}>
        <strong>Deliver:</strong> {action.deliverable}
      </p>
      <p style={{ fontSize: 12, color: '#16a34a', margin: '0 0 10px', fontWeight: 600 }}>
        ✓ Done when: {action.definition_of_done}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {action.dimensions_improved && action.dimensions_improved.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {action.dimensions_improved.map(d => (
              <span
                key={d}
                style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 10,
                  background: '#eef2ff', color: '#6366f1', fontWeight: 600,
                }}
              >
                ↑ {BREAKDOWN_FULL_LABELS[d] || d}
              </span>
            ))}
          </div>
        )}
        <Link
          to="/resumes"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
            fontSize: 12, fontWeight: 700, color: '#fff', textDecoration: 'none',
            background: accent, borderRadius: 8, padding: '5px 14px',
          }}
        >
          Start now <FiArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
}

// ── Insight Radar ──────────────────────────────────────────────────────────────

function InsightRadar({ radarScores }) {
  if (!radarScores) return null;
  const data = BREAKDOWN_ORDER
    .filter(k => radarScores[k])
    .map(k => ({
      axis: BREAKDOWN_LABELS[k],
      value: Math.round((radarScores[k].score || 0) * 10),
    }));
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <RadarChart cx="50%" cy="50%" outerRadius="68%" data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: '#64748b' }} />
        <Radar dataKey="value" stroke={TEAL} fill={TEAL} fillOpacity={0.3} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function DiagnosisReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const [diagnosis, setDiagnosis] = useState(location.state?.diagnosis || null);
  const [loading, setLoading] = useState(!location.state?.diagnosis && !location.state?.error);
  const resumeIdFromNav = location.state?.resumeId;

  useEffect(() => {
    if (location.state?.diagnosis || location.state?.error) return;
    const req = resumeIdFromNav
      ? diagnosisApi.getForResume(resumeIdFromNav)
      : diagnosisApi.getLatest();
    req
      .then(res => setDiagnosis(res.data))
      .catch(() => setDiagnosis(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <LoadingSpinner message="Loading your diagnosis…" />;

  if (!diagnosis) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center', padding: '0 16px' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🩺</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
          No diagnosis yet
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
          Get your Resume Diagnosis in 5 minutes — we&apos;ll tell you exactly what&apos;s holding your job search back.
        </p>
        <Link
          to="/resume-diagnosis"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 10, background: TEAL, color: '#fff',
            textDecoration: 'none', fontWeight: 700, fontSize: 14,
          }}
        >
          Start Quick Entry <FiArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const {
    overall_score, overall_status, overall_label,
    radar_scores = {}, top_issues = [], fix_plan = [],
    profile = {},
  } = diagnosis;

  const profileRoles = (profile.target_roles || []).join(', ') || '—';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 48px' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: '#64748b', padding: 0,
          }}
        >
          <FiArrowLeft size={16} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: 0 }}>
            🩺 Your Resume Medical Checkup Report
          </h1>
          {profileRoles !== '—' && (
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '3px 0 0' }}>
              Target: <strong>{profileRoles}</strong> · {profile.seniority} · {profile.location}
            </p>
          )}
        </div>
      </div>

      <div style={{
        background: '#fff', borderRadius: 14, padding: '24px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16,
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
          <ScoreGauge score={overall_score} status={overall_status} size={120} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <StatusBadge status={overall_status} label={overall_label} />
            <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', margin: '6px 0 4px' }}>
              {overall_score} / 100
            </h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0, maxWidth: 340 }}>
              Weighted across 6 resume dimensions. Skill Coverage, Impact Quantification,
              and Professional Relevance carry the most weight.
            </p>
          </div>
          <div style={{ flexShrink: 0, width: 220 }}>
            <InsightRadar radarScores={radar_scores} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {BREAKDOWN_ORDER.map(key => {
            const dim = radar_scores[key] || {};
            return (
              <DimCard
                key={key}
                dimKey={key}
                label={BREAKDOWN_LABELS[key]}
                score={typeof dim.score === 'number' ? dim.score : 0}
                status={dim.status || 'red'}
                explanation={dim.explanation || '—'}
              />
            );
          })}
        </div>
      </div>

      <div style={{
        background: '#fff', borderRadius: 14, padding: '24px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16,
      }}
      >
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: 0 }}>
            🔍 Top 3 Issues — Found in YOUR Resume
          </h3>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>
            Ranked by impact on interview probability. Click any issue to see evidence.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {top_issues.length > 0
            ? top_issues.map((issue, i) => <IssueCard key={i} issue={issue} rank={i + 1} />)
            : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                <FiAlertCircle size={28} />
                <p style={{ marginTop: 8 }}>No issues found — re-diagnose with more resume detail.</p>
              </div>
            )}
        </div>
      </div>

      {fix_plan.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 14, padding: '24px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16,
        }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>
            📅 Next 7 Days Fix Plan
          </h3>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 16px' }}>
            Stop applying. Fix these first.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {fix_plan.map((action, i) => <FixCard key={i} action={action} index={i} />)}
          </div>
        </div>
      )}

      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f, #0f766e)',
        borderRadius: 14, padding: '28px 28px 24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)', marginBottom: 16,
      }}
      >
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.15)', borderRadius: 20,
          padding: '4px 12px', marginBottom: 12,
        }}
        >
          <span style={{ fontSize: 11, color: '#e2f8f5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Most Important Next Step
          </span>
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 8px', lineHeight: 1.3 }}>
          Your diagnosis is 60% accurate. Feed me data to fix that.
        </h3>
        <p style={{ fontSize: 13, color: '#b2f0e8', margin: '0 0 16px', lineHeight: 1.6, maxWidth: 560 }}>
          We can tell your resume has issues — but we can&apos;t tell if that&apos;s <em>why</em> you&apos;re getting ghosted, or
          if you&apos;re just applying through the wrong channel. Import 10 application records in 30 seconds and we&apos;ll know.
        </p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { q: 'Zero replies on 50+ apps?', a: '→ Targeting or channel problem' },
            { q: 'Calls but ghosted after?', a: '→ Resume or prep problem' },
            { q: 'Applied to < 10 jobs?', a: '→ Volume problem' },
          ].map(({ q, a }) => (
            <div
              key={q}
              style={{
                background: 'rgba(255,255,255,0.1)', borderRadius: 8,
                padding: '8px 12px', flex: 1, minWidth: 160,
              }}
            >
              <p style={{ fontSize: 12, color: '#e2f8f5', margin: 0, fontWeight: 600 }}>{q}</p>
              <p style={{ fontSize: 11, color: '#b2f0e8', margin: '2px 0 0' }}>{a}</p>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {['Paste rows', 'Upload CSV', 'Sync LinkedIn', 'Sync Simplify'].map(opt => (
            <button
              key={opt}
              type="button"
              style={{
                padding: '7px 14px', borderRadius: 20,
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: '#e2f8f5', fontSize: 12, cursor: 'pointer', fontWeight: 600,
              }}
            >
              {opt}
            </button>
          ))}
        </div>

        <Link
          to="/applications"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 10,
            background: '#fff', color: '#0f766e',
            textDecoration: 'none', fontWeight: 800, fontSize: 14,
          }}
        >
          <FiUpload size={15} />
          Feed Me Data → Sharpen My Diagnosis
        </Link>
        <p style={{ fontSize: 11, color: '#b2f0e8', marginTop: 10, marginBottom: 0 }}>
          30 seconds. Completely private. No company names stored — just counts and stages.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Link
          to="/profile"
          style={{
            flex: 1, minWidth: 200, padding: '14px 18px', borderRadius: 10,
            border: '2px solid #e2e8f0', background: '#fff',
            textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', margin: 0 }}>Improve Profile</p>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0' }}>Boosts Relevance + Narrative scores</p>
          </div>
          <FiArrowRight size={14} color="#94a3b8" />
        </Link>
        <Link
          to="/resumes"
          style={{
            flex: 1, minWidth: 200, padding: '14px 18px', borderRadius: 10,
            border: '2px solid #e2e8f0', background: '#fff',
            textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', margin: 0 }}>Generate Tailored Resume</p>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0' }}>For a specific JD · uses credits</p>
          </div>
          <FiArrowRight size={14} color="#94a3b8" />
        </Link>
        <p style={{ width: '100%', fontSize: 11, color: '#94a3b8', margin: '4px 0 0', textAlign: 'center' }}>
          Do these <em>after</em> importing records — the diagnosis gets smarter first.
        </p>
      </div>

    </div>
  );
}
